import type { Meeting } from "@scoach/types";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

import { api } from "../../../../lib/http.ts";
import { useAudioCapture } from "../../../meeting/live/audio/useAudioCapture.ts";
import { useAudioUploader } from "../../../meeting/live/audio/useAudioUploader.ts";
import { useMicCapture } from "../../../meeting/live/audio/useMicCapture.ts";
import { useScreenCapture } from "../../../meeting/live/audio/useScreenCapture.ts";
import { ResizableMain } from "../../../meeting/live/components/ResizableMain.tsx";
import { useLiveMeeting } from "../../../meeting/live/hooks/useLiveMeeting.ts";
import { useLiveMeetingStore } from "../../../meeting/live/store.ts";
import { interviewApi } from "../../api.ts";
import { useInterviewPacing } from "../hooks/useInterviewPacing.ts";
import { useInterviewLiveStore } from "../store.ts";
import { CvViewerModal } from "./CvViewerModal.tsx";
import { InterviewCoachColumn } from "./InterviewCoachColumn.tsx";
import { InterviewContextRail } from "./InterviewContextRail.tsx";
import { InterviewRightPanel } from "./InterviewRightPanel.tsx";
import { PacingBanner } from "./PacingBanner.tsx";
import { PhaseTimeline } from "./PhaseTimeline.tsx";

interface CvInfo {
  cvFileName?: string;
  cvContext?: string;
  hasFile: boolean;
}

/**
 * Rich interview live screen — a copy of the sales LiveMeetingScreen adapted for
 * interviewers. Reuses the meeting live store/hook + ResizableMain + screen-share
 * preview + transcript panel; adds interview coaching (answer assessments, tips,
 * follow-ups), a live scorecard, question checklist and structural pacing.
 */
export function InterviewLiveScreen({ meetingId }: { meetingId: string }) {
  // Rich shared live data (transcript/hints/tips/followups/partial) via the reused hook.
  useLiveMeeting(meetingId);
  const nav = useNavigate();
  const pacing = useInterviewPacing();

  const listening = useLiveMeetingStore((s) => s.listening);
  const setListening = useLiveMeetingStore((s) => s.setListening);
  const muted = useLiveMeetingStore((s) => s.muted);
  const setMuted = useLiveMeetingStore((s) => s.setMuted);

  const setChecklist = useInterviewLiveStore((s) => s.setChecklist);
  const checklist = useInterviewLiveStore((s) => s.checklist);

  const [ending, setEnding] = useState(false);
  const [cv, setCv] = useState<CvInfo | null>(null);
  const [cvOpen, setCvOpen] = useState(false);

  const tabUploader = useAudioUploader({ meetingId, source: "tab" });
  const micUploader = useAudioUploader({ meetingId, source: "mic" });

  const handleTabFrame = useCallback(
    (frame: { pcm: Int16Array }) => tabUploader.push(frame.pcm),
    [tabUploader],
  );
  const handleMicFrame = useCallback(
    (frame: { pcm: Int16Array }) => micUploader.push(frame.pcm),
    [micUploader],
  );

  const audio = useAudioCapture({ onFrame: handleTabFrame, paused: muted });
  const mic = useMicCapture({ onFrame: handleMicFrame, paused: muted });
  // Screen share preview + periodic frames (interviews: preview + screenshots only;
  // the server gates sales screen-hint analysis off for interview meetings).
  useScreenCapture({ stream: audio.stream, meetingId });

  // Seed the question checklist from the setup metadata.
  useEffect(() => {
    let cancelled = false;
    interviewApi
      .fetchMeeting(meetingId)
      .then((m: Meeting) => {
        if (cancelled) return;
        const qs = m.interview?.customQuestions ?? [];
        setChecklist(qs.map((q) => ({ id: q.id, text: q.text, asked: false, origin: q.origin })));
        const iv = m.interview;
        if (iv && (iv.cvContext || iv.cvGcsPath)) {
          setCv({
            cvFileName: iv.cvFileName,
            cvContext: iv.cvContext,
            hasFile: Boolean(iv.cvGcsPath),
          });
        }
        // Pre-load the live summary from the CV so it's already populated when
        // the interviewer opens the screen (before pressing Start).
        if (iv?.cvContext) {
          void interviewApi.ensureCvSummary(meetingId).catch(() => {});
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [meetingId, setChecklist]);

  const start = useCallback(() => {
    setListening(true);
    void mic.start();
    void audio.start(); // prompts to share the call tab/window (candidate audio + screen preview)
  }, [mic, audio, setListening]);

  const startShare = useCallback(() => {
    void audio.start();
  }, [audio]);
  const stopShare = useCallback(() => {
    audio.stop();
  }, [audio]);

  const end = useCallback(async () => {
    setEnding(true);
    mic.stop();
    audio.stop();
    try {
      await Promise.race([
        Promise.all([tabUploader.flush(), micUploader.flush()]),
        new Promise((r) => setTimeout(r, 3000)),
      ]);
    } catch {
      /* best-effort flush */
    }
    void api(`/meetings/${meetingId}/end`, { method: "POST", body: {} }).catch(() => {});
    nav({ to: "/interviews/$id/summary", params: { id: meetingId } });
  }, [mic, audio, tabUploader, micUploader, meetingId, nav]);

  const askedCount = checklist.filter((c) => c.asked).length;

  return (
    <div className="app">
      {/* Slim interview header */}
      <header
        className="topbar"
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        <div className="topbar-left" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img src="/supercloud-mark.svg" alt="SellSynq" width={26} height={26} />
          <div style={{ fontWeight: 700 }}>
            Interview <span style={{ color: "#7627bb" }}>· candidate = TC</span>
          </div>
          <span style={{ fontSize: 12, color: "var(--text-3)" }}>
            {askedCount}/{checklist.length} asked · {listening ? "LIVE" : "not started"}
          </span>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {!listening ? (
            <button type="button" className="pill-btn primary" onClick={start}>
              Start interview
            </button>
          ) : (
            <>
              <button type="button" className="pill-btn" onClick={() => setMuted(!muted)}>
                {muted ? "Unmute" : "Mute"}
              </button>
              <button type="button" className="pill-btn primary" onClick={end} disabled={ending}>
                {ending ? "Ending…" : "End & evaluate"}
              </button>
            </>
          )}
        </div>
      </header>

      <PhaseTimeline pacing={pacing} />
      <PacingBanner pacing={pacing} />

      <ResizableMain
        rail={
          <InterviewContextRail
            meetingId={meetingId}
            stream={audio.stream}
            rmsDb={audio.rmsDb}
            error={audio.error}
            onPickSource={startShare}
            onStopShare={stopShare}
            micCapturing={mic.capturing}
            micError={mic.error}
            hasCv={Boolean(cv)}
            onViewResume={() => setCvOpen(true)}
          />
        }
        coach={<InterviewCoachColumn meetingId={meetingId} />}
        transcript={<InterviewRightPanel meetingId={meetingId} />}
      />

      {cvOpen && cv && (
        <CvViewerModal
          meetingId={meetingId}
          cvFileName={cv.cvFileName}
          cvContext={cv.cvContext}
          hasFile={cv.hasFile}
          onClose={() => setCvOpen(false)}
        />
      )}
    </div>
  );
}
